/*
 * <cart-upsell> — a single add-on toggle (e.g. Shipping Protection) in the cart drawer.
 *
 * ON  -> POST /cart/add.js to add the configured variant (qty 1, locked).
 * OFF -> look up the line key via /cart.js, then POST /cart/change.js qty 0.
 *
 * The checked state is rendered server-side from whether the variant is in the cart,
 * so after every change we publish PUB_SUB_EVENTS.cartUpdate and let the theme's
 * existing cart-drawer-items subscriber re-render the drawer (incl. this toggle).
 * Only the cart icon bubble is refreshed here, since that subscriber skips it.
 */
class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.checkbox = this.querySelector('.cart-upsell__checkbox');
    this.errorEl = this.querySelector('.cart-upsell__error');

    if (this.checkbox) {
      this.checkbox.addEventListener('change', this.onToggle.bind(this));
    }
  }

  connectedCallback() {
    // Cart now holds the add-on but no real items left -> remove it so it can't be
    // checked out alone. Runs once when the freshly rendered element connects.
    if (this.dataset.orphaned === 'true' && !this.loading) {
      this.removeUpsell(true);
    }
  }

  get variantId() {
    return this.dataset.variantId;
  }

  onToggle() {
    if (this.loading) {
      // Ignore rapid double-clicks; revert the visual state until the request settles.
      this.checkbox.checked = !this.checkbox.checked;
      return;
    }
    this.clearError();
    if (this.checkbox.checked) {
      this.addUpsell();
    } else {
      this.removeUpsell(false);
    }
  }

  addUpsell() {
    const body = {
      id: this.variantId,
      quantity: 1,
      sections: ['cart-icon-bubble'],
      sections_url: window.location.pathname,
    };

    const propName = this.dataset.propertyName;
    if (propName) {
      body.properties = {};
      body.properties[propName] = this.dataset.propertyValue || 'Yes';
    }

    this.setLoading(true);
    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: JSON.stringify(body) })
      .then((response) => response.json())
      .then((parsed) => {
        if (parsed.status) {
          // Shopify returns { status, message, description } on failure.
          throw new Error(parsed.description || parsed.message);
        }
        return this.finishUpdate(parsed);
      })
      .catch((error) => this.handleError(error, false));
  }

  removeUpsell(silent) {
    this.setLoading(true);
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const line = cart.items.find((item) => String(item.variant_id) === String(this.variantId));
        if (!line) {
          // Already gone; just resync the UI.
          return this.finishUpdate(cart);
        }
        const body = {
          id: line.key,
          quantity: 0,
          sections: ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        };
        return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body: JSON.stringify(body) })
          .then((response) => response.json())
          .then((parsed) => this.finishUpdate(parsed));
      })
      .catch((error) => this.handleError(error, silent));
  }

  finishUpdate(parsed) {
    this.refreshCartBubble(parsed.sections);
    // Re-render of the drawer + main cart happens in the theme's cartUpdate subscribers.
    // This element lives inside .cart-drawer__footer and will be replaced, so don't
    // touch `this` after publishing.
    return publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cartData: parsed });
  }

  refreshCartBubble(sections) {
    if (!sections || !sections['cart-icon-bubble']) return;
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const parsed = new DOMParser()
      .parseFromString(sections['cart-icon-bubble'], 'text/html')
      .querySelector('.shopify-section');
    if (parsed) bubble.innerHTML = parsed.innerHTML;
  }

  setLoading(state) {
    this.loading = state;
    this.classList.toggle('cart-upsell--loading', state);
    if (this.checkbox) this.checkbox.disabled = state;
  }

  clearError() {
    if (this.errorEl) this.errorEl.textContent = '';
  }

  handleError(error, silent) {
    console.error('Cart upsell:', error);
    this.setLoading(false);
    if (this.checkbox) this.checkbox.checked = !this.checkbox.checked;
    if (!silent && this.errorEl) {
      const fallback = window.cartStrings && window.cartStrings.error ? window.cartStrings.error : 'Something went wrong. Please try again.';
      this.errorEl.textContent = fallback;
    }
  }
}

customElements.define('cart-upsell', CartUpsell);
